/**
 * Labour Invoice Tab — Phase 3 + Slice 3A.
 * Two sub-tabs: Invoices + Advance Payments.
 * Creating a labour invoice:
 *   - Contractor name with auto-suggest (from past advances + past invoices)
 *   - Live "Advance Available" badge + Apply Advance input (defaults to min(available, subtotal-tds))
 *   - 1% TDS default (editable)
 *   - Net Payable = Subtotal − TDS − Advance Applied (can be NEGATIVE = refund owed)
 * Advance Payments tab:
 *   - Log advances per contractor; shows Remaining balance per entry
 *   - Cannot delete an advance if used_amount > 0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Save, Users, Trash2, FileText, Wallet, AlertCircle } from 'lucide-react';
import { siteProjectAPI } from '../../services/api';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- Advance Dialog ----
const AdvanceDialog = ({ project, suggestions, onClose, onSaved }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), contractor_name: '', amount: 0, remarks: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (!form.contractor_name.trim()) { setError('Contractor name required'); return; }
    if (!form.amount || form.amount <= 0) { setError('Amount must be > 0'); return; }
    setSaving(true);
    try {
      await siteProjectAPI.createLabourAdvance(project.id, { project_id: project.id, ...form });
      onSaved();
    } catch (e) { setError(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Add Labour Advance</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Date *</label>
            <input data-testid="adv-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Contractor *</label>
            <input data-testid="adv-contractor" list="advContractorList" value={form.contractor_name} onChange={e => setForm({ ...form, contractor_name: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Type or pick from suggestions" />
            <datalist id="advContractorList">{(suggestions || []).map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Amount (₹) *</label>
            <input data-testid="adv-amount" type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
          <button data-testid="adv-save-btn" onClick={save} disabled={saving} className="px-4 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Labour Invoice Dialog ----
const InvoiceDialog = ({ project, suggestions, onClose, onSaved }) => {
  const [mode, setMode] = useState('qty');  // 'qty' or 'manual'
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    contractor_name: '', contractor_pan: '', contractor_phone: '',
    description: '', subtotal: 0, tds_rate: 1, advance_applied: 0, remarks: '',
    // qty-mode fields
    total_quantity: 0, rate_per_unit: Number(project.labour_rate_per_unit) || 0,
    date_from: '', date_to: '',
  });
  const [balance, setBalance] = useState({ available: 0, entries: [] });
  const [pendingSummary, setPendingSummary] = useState({ pending_qty: 0, rows: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Track whether the user has manually touched the advance field — reset per-contractor
  const [advTouched, setAdvTouched] = useState(false);
  useEffect(() => { setAdvTouched(false); }, [form.contractor_name]);

  // Fetch advance balance when contractor changes
  useEffect(() => {
    const name = form.contractor_name.trim();
    if (!name) { setBalance({ available: 0, entries: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await siteProjectAPI.labourAdvanceBalance(project.id, name);
        setBalance(r || { available: 0, entries: [] });
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [form.contractor_name, project.id]);

  // Fetch pending-work summary for qty mode
  useEffect(() => {
    if (mode !== 'qty') return;
    const t = setTimeout(async () => {
      try {
        const r = await siteProjectAPI.pendingWorkSummary(project.id, { date_from: form.date_from, date_to: form.date_to });
        setPendingSummary(r?.labour || { pending_qty: 0, rows: [] });
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [mode, project.id, form.date_from, form.date_to]);

  // FIFO allocation preview (qty mode)
  const plan = (() => {
    if (mode !== 'qty') return { items: [], short: 0 };
    const out = [];
    let left = Number(form.total_quantity) || 0;
    for (const r of pendingSummary.rows) {
      if (left <= 0) break;
      const take = Math.min(left, r.remaining);
      out.push({ id: r.id, date: r.date, consumed: Math.round(take * 1000) / 1000, total: r.total, remaining_after: Math.round((r.remaining - take) * 1000) / 1000, unit: r.unit });
      left -= take;
    }
    return { items: out, short: left > 0.001 ? Math.round(left * 1000) / 1000 : 0 };
  })();

  const sub = mode === 'qty'
    ? Math.round((Number(form.total_quantity) || 0) * (Number(form.rate_per_unit) || 0) * 100) / 100
    : Number(form.subtotal) || 0;
  const tdsRate = Number(form.tds_rate) || 0;
  const tds_amount = Math.round(sub * tdsRate / 100 * 100) / 100;
  const advance_applied = Number(form.advance_applied) || 0;
  const net_payable = Math.round((sub - tds_amount - advance_applied) * 100) / 100;
  const isRefund = net_payable < 0;

  // Auto-suggest advance when subtotal/tds/balance changes (until user touches manually)
  useEffect(() => {
    if (advTouched) return;
    const suggest = Math.min(balance.available, Math.max(0, sub - tds_amount));
    setForm(f => ({ ...f, advance_applied: Math.round(suggest * 100) / 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance.available, sub, tds_amount]);

  const save = async () => {
    setError('');
    if (!form.contractor_name.trim()) { setError('Contractor name required'); return; }
    if (mode === 'qty') {
      if (!(Number(form.total_quantity) > 0)) { setError('Enter invoiced qty > 0'); return; }
      if (!(Number(form.rate_per_unit) > 0)) { setError('Enter rate per unit > 0'); return; }
      if (plan.short > 0) { setError(`Short by ${fmt(plan.short)}. Reduce qty or expand date range.`); return; }
    } else {
      if (!(sub > 0)) { setError('Subtotal must be > 0'); return; }
    }
    if (advance_applied > balance.available + 0.001) { setError(`Advance applied (₹${fmt(advance_applied)}) exceeds available (₹${fmt(balance.available)}).`); return; }
    setSaving(true);
    try {
      const payload = { project_id: project.id, ...form };
      if (mode === 'manual') {
        payload.total_quantity = 0;
        payload.rate_per_unit = 0;
      } else {
        payload.subtotal = 0;  // server computes from qty × rate
      }
      // Blank empty dates
      if (!payload.date_from) payload.date_from = null;
      if (!payload.date_to) payload.date_to = null;
      await siteProjectAPI.createLabourInvoice(project.id, payload);
      onSaved();
    } catch (e) { setError(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Create Labour Invoice</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Mode toggle */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
            <button data-testid="li-mode-qty" onClick={() => setMode('qty')} className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === 'qty' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
              From Work Qty (FIFO)
            </button>
            <button data-testid="li-mode-manual" onClick={() => setMode('manual')} className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${mode === 'manual' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}>
              Manual Subtotal
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Date *</label>
              <input data-testid="li-date" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Contractor *</label>
              <input data-testid="li-contractor" list="liContractorList" value={form.contractor_name} onChange={e => setForm(f => ({ ...f, contractor_name: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Type or pick" />
              <datalist id="liContractorList">{(suggestions || []).map(s => <option key={s} value={s} />)}</datalist>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">PAN</label>
              <input data-testid="li-pan" value={form.contractor_pan} onChange={e => setForm(f => ({ ...f, contractor_pan: e.target.value.toUpperCase() }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Optional" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Phone</label>
              <input value={form.contractor_phone} onChange={e => setForm(f => ({ ...f, contractor_phone: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="Optional" />
            </div>
          </div>

          {form.contractor_name.trim() && (
            <div className={`p-2 rounded-lg text-xs ${balance.available > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`} data-testid="li-advance-badge">
              <div className="flex justify-between items-center">
                <span className={balance.available > 0 ? 'text-emerald-700 font-semibold' : 'text-gray-500'}>
                  <Wallet className="w-3.5 h-3.5 inline mr-1" />Advance Available for {form.contractor_name.trim()}
                </span>
                <span className={`font-bold ${balance.available > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>₹{fmt(balance.available)}</span>
              </div>
            </div>
          )}

          {mode === 'qty' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Date From</label>
                  <input data-testid="li-date-from" type="date" value={form.date_from} onChange={e => setForm(f => ({ ...f, date_from: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Date To</label>
                  <input data-testid="li-date-to" type="date" value={form.date_to} onChange={e => setForm(f => ({ ...f, date_to: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Pending Qty</label>
                  <p className="px-2 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-lg" data-testid="li-pending-qty">{fmt(pendingSummary.pending_qty)} {pendingSummary.rows[0]?.unit || ''}</p>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Rate / Unit *</label>
                  <input data-testid="li-rate" type="number" value={form.rate_per_unit || ''} onChange={e => setForm(f => ({ ...f, rate_per_unit: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Invoiced Qty *</label>
                <input data-testid="li-qty" type="number" value={form.total_quantity || ''} onChange={e => setForm(f => ({ ...f, total_quantity: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
              </div>
              {Number(form.total_quantity) > 0 && (
                <div className="border border-blue-100 rounded-lg overflow-hidden" data-testid="li-allocation-preview">
                  <div className="bg-blue-50 px-3 py-1.5 text-[10px] font-bold text-blue-700 uppercase">
                    FIFO allocation {plan.short > 0 && <span className="text-red-600 font-normal">— short by {fmt(plan.short)}</span>}
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-blue-50/50 text-[10px] text-blue-500 uppercase"><th className="text-left py-1.5 px-3">Work Date</th><th className="text-right py-1.5 px-3">Consumed</th><th className="text-right py-1.5 px-3">Remaining After</th></tr></thead>
                    <tbody>
                      {plan.items.map(p => <tr key={p.id} className="border-t border-blue-50"><td className="py-1.5 px-3 text-gray-600">{p.date}</td><td className="py-1.5 px-3 text-right font-semibold text-blue-700">{fmt(p.consumed)}</td><td className="py-1.5 px-3 text-right text-gray-500">{fmt(p.remaining_after)}</td></tr>)}
                      {plan.items.length === 0 && <tr><td colSpan={3} className="py-3 px-3 text-center text-gray-400">No allocations yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {mode === 'manual' && (
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Subtotal (₹) *</label>
              <input data-testid="li-subtotal" type="number" value={form.subtotal || ''} onChange={e => setForm(f => ({ ...f, subtotal: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          )}

          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Description</label>
            <input data-testid="li-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" placeholder="e.g. Thermoplastic marking Feb 23" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">TDS %</label>
              <input data-testid="li-tds-rate" type="number" step="0.1" value={form.tds_rate} onChange={e => setForm(f => ({ ...f, tds_rate: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Apply Advance (₹)</label>
              <input data-testid="li-advance-applied" type="number" value={form.advance_applied || ''} onChange={e => { setAdvTouched(true); setForm(f => ({ ...f, advance_applied: parseFloat(e.target.value) || 0 })); }} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          </div>

          <div className="p-3 bg-amber-50 rounded-lg text-xs space-y-1">
            <div className="flex justify-between text-amber-700"><span>Subtotal</span><span className="font-semibold">₹{fmt(sub)}</span></div>
            <div className="flex justify-between text-amber-700"><span>TDS ({tdsRate}%)</span><span>− ₹{fmt(tds_amount)}</span></div>
            {advance_applied > 0 && (
              <div className="flex justify-between text-amber-700"><span>Advance Applied</span><span>− ₹{fmt(advance_applied)}</span></div>
            )}
            <div className="flex justify-between pt-1 border-t border-amber-200">
              <span className={`font-bold ${isRefund ? 'text-red-700' : 'text-amber-800'}`}>{isRefund ? 'Refund Owed (by contractor)' : 'Net Payable'}</span>
              <span className={`font-bold ${isRefund ? 'text-red-700' : 'text-amber-800'}`} data-testid="li-net">₹{fmt(Math.abs(net_payable))}{isRefund ? ' CR' : ''}</span>
            </div>
          </div>

          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={2} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>

          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-start gap-2"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
          <button data-testid="li-save-btn" onClick={save} disabled={saving} className="px-4 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? 'Saving…' : 'Save Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Main Tab ----
export const LabourInvoiceTab = ({ project }) => {
  const [subtab, setSubtab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invDialog, setInvDialog] = useState(false);
  const [advDialog, setAdvDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, adv, sug] = await Promise.all([
        siteProjectAPI.listLabourInvoices(project.id),
        siteProjectAPI.listLabourAdvances(project.id),
        siteProjectAPI.contractorSuggestions(project.id),
      ]);
      setInvoices(inv || []);
      setAdvances(adv || []);
      setSuggestions(sug || []);
    } finally { setLoading(false); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const delInv = async (id) => {
    if (!window.confirm('Delete this invoice? Applied advances will be restored.')) return;
    await siteProjectAPI.deleteLabourInvoice(project.id, id); load();
  };
  const delAdv = async (id) => {
    if (!window.confirm('Delete this advance entry?')) return;
    try { await siteProjectAPI.deleteLabourAdvance(project.id, id); load(); }
    catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  const totalSub = invoices.reduce((s, i) => s + (Number(i.subtotal) || 0), 0);
  const totalTds = invoices.reduce((s, i) => s + (Number(i.tds_amount) || 0), 0);
  const totalAdv = invoices.reduce((s, i) => s + (Number(i.advance_applied) || 0), 0);
  const totalNet = invoices.reduce((s, i) => s + (Number(i.net_payable) || 0), 0);

  const totalAdvAmt = advances.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const totalAdvUsed = advances.reduce((s, a) => s + (Number(a.used_amount) || 0), 0);
  const totalAdvRem = totalAdvAmt - totalAdvUsed;

  return (
    <div className="space-y-4" data-testid="labour-invoice-tab">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Users className="w-4 h-4" /> Labour Payments</h4>
          <p className="text-[11px] text-gray-400">Default 1% TDS · Advances auto-apply per contractor</p>
        </div>
        <div className="flex items-center gap-2">
          {subtab === 'invoices' ? (
            <button data-testid="li-add-btn" onClick={() => setInvDialog(true)} className="px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1" style={{ backgroundColor: '#24549c' }}>
              <Plus className="w-3.5 h-3.5" /> Create Invoice
            </button>
          ) : (
            <button data-testid="adv-add-btn" onClick={() => setAdvDialog(true)} className="px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1" style={{ backgroundColor: '#24549c' }}>
              <Plus className="w-3.5 h-3.5" /> Add Advance
            </button>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-gray-100">
        <button data-testid="li-subtab-invoices" onClick={() => setSubtab('invoices')} className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${subtab === 'invoices' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>Invoices</button>
        <button data-testid="li-subtab-advances" onClick={() => setSubtab('advances')} className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${subtab === 'advances' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
          Advance Payments {advances.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-[9px] rounded-full bg-amber-100 text-amber-700">{advances.length}</span>}
        </button>
      </div>

      {subtab === 'invoices' && (
        <>
          {invoices.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              <div className="p-3 bg-blue-50 rounded-lg"><p className="text-[10px] text-blue-600 uppercase">Gross</p><p className="text-sm font-bold text-blue-700">₹{fmt(totalSub)}</p></div>
              <div className="p-3 bg-red-50 rounded-lg"><p className="text-[10px] text-red-500 uppercase">TDS</p><p className="text-sm font-bold text-red-600">− ₹{fmt(totalTds)}</p></div>
              <div className="p-3 bg-amber-50 rounded-lg"><p className="text-[10px] text-amber-600 uppercase">Advance Used</p><p className="text-sm font-bold text-amber-700">− ₹{fmt(totalAdv)}</p></div>
              <div className={`p-3 rounded-lg ${totalNet < 0 ? 'bg-red-50' : 'bg-emerald-50'}`}><p className={`text-[10px] uppercase ${totalNet < 0 ? 'text-red-500' : 'text-emerald-600'}`}>{totalNet < 0 ? 'Net Refund Due' : 'Net Paid'}</p><p className={`text-sm font-bold ${totalNet < 0 ? 'text-red-700' : 'text-emerald-700'}`}>₹{fmt(Math.abs(totalNet))}{totalNet < 0 ? ' CR' : ''}</p></div>
            </div>
          )}

          {loading ? <p className="text-xs text-gray-400">Loading…</p> : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-400"><FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No labour invoices yet.</p></div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
              <table className="w-full text-xs min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                    <th className="text-left py-2 px-3">Invoice #</th>
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Contractor</th>
                    <th className="text-right py-2 px-3">Subtotal</th>
                    <th className="text-right py-2 px-3">TDS</th>
                    <th className="text-right py-2 px-3">Advance Used</th>
                    <th className="text-right py-2 px-3">Net</th>
                    <th className="text-right py-2 px-3 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} data-testid={`li-row-${inv.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 px-3 font-semibold text-gray-800">{inv.invoice_number}</td>
                      <td className="py-2 px-3 text-gray-600">{inv.date}</td>
                      <td className="py-2 px-3">{inv.contractor_name}{inv.contractor_pan && <span className="block text-[10px] text-gray-400">PAN: {inv.contractor_pan}</span>}</td>
                      <td className="py-2 px-3 text-right">₹{fmt(inv.subtotal)}</td>
                      <td className="py-2 px-3 text-right text-red-600">− ₹{fmt(inv.tds_amount)} <span className="text-[10px] text-gray-400">({inv.tds_rate}%)</span></td>
                      <td className="py-2 px-3 text-right text-amber-600">{(inv.advance_applied || 0) > 0 ? `− ₹${fmt(inv.advance_applied)}` : '—'}</td>
                      <td className={`py-2 px-3 text-right font-bold ${inv.net_payable < 0 ? 'text-red-600' : 'text-emerald-700'}`}>₹{fmt(Math.abs(inv.net_payable))}{inv.net_payable < 0 ? ' CR' : ''}</td>
                      <td className="py-2 px-3 text-right">
                        <button data-testid={`li-delete-${inv.id}`} onClick={() => delInv(inv.id)} className="text-red-500 hover:underline text-[11px]"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {subtab === 'advances' && (
        <>
          {advances.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 bg-blue-50 rounded-lg"><p className="text-[10px] text-blue-600 uppercase">Total Paid</p><p className="text-sm font-bold text-blue-700">₹{fmt(totalAdvAmt)}</p></div>
              <div className="p-3 bg-amber-50 rounded-lg"><p className="text-[10px] text-amber-600 uppercase">Used</p><p className="text-sm font-bold text-amber-700">₹{fmt(totalAdvUsed)}</p></div>
              <div className="p-3 bg-emerald-50 rounded-lg"><p className="text-[10px] text-emerald-600 uppercase">Remaining</p><p className="text-sm font-bold text-emerald-700">₹{fmt(totalAdvRem)}</p></div>
            </div>
          )}
          {advances.length === 0 ? (
            <div className="text-center py-8 text-gray-400"><Wallet className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No advances logged yet.</p></div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Contractor</th>
                    <th className="text-right py-2 px-3">Amount</th>
                    <th className="text-right py-2 px-3">Used</th>
                    <th className="text-right py-2 px-3">Remaining</th>
                    <th className="text-left py-2 px-3">Remarks</th>
                    <th className="text-right py-2 px-3 w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {advances.map(a => (
                    <tr key={a.id} data-testid={`adv-row-${a.id}`} className="border-b border-gray-50">
                      <td className="py-2 px-3 text-gray-600">{a.date}</td>
                      <td className="py-2 px-3 font-semibold">{a.contractor_name}</td>
                      <td className="py-2 px-3 text-right">₹{fmt(a.amount)}</td>
                      <td className="py-2 px-3 text-right text-amber-600">₹{fmt(a.used_amount)}</td>
                      <td className="py-2 px-3 text-right font-bold text-emerald-700">₹{fmt(a.remaining_amount)}</td>
                      <td className="py-2 px-3 text-[11px] text-gray-400">{a.remarks || '—'}</td>
                      <td className="py-2 px-3 text-right">
                        {Number(a.used_amount) > 0 ? <span className="text-[10px] text-gray-400">Locked</span> : (
                          <button data-testid={`adv-delete-${a.id}`} onClick={() => delAdv(a.id)} className="text-red-500 hover:underline text-[11px]"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {invDialog && <InvoiceDialog project={project} suggestions={suggestions} onClose={() => setInvDialog(false)} onSaved={() => { setInvDialog(false); load(); }} />}
      {advDialog && <AdvanceDialog project={project} suggestions={suggestions} onClose={() => setAdvDialog(false)} onSaved={() => { setAdvDialog(false); load(); }} />}
    </div>
  );
};

export default LabourInvoiceTab;
